import { useUsers } from "@/http/api-hooks";
import { User } from "@/http/api-types";
import { useEffect, useRef, useState } from "react";

interface Props {
  editorRef: React.RefObject<HTMLDivElement | null>;
  onInsert: (user: User) => void;
}

interface CaretRect {
  top: number;
  left: number;
}

function getCaretRect(): CaretRect | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;
  const range = sel.getRangeAt(0).cloneRange();
  range.collapse(true);
  const rects = range.getClientRects();
  if (!rects.length) return null;
  return { top: rects[0].bottom, left: rects[0].left };
}

function getQueryBeforeCaret(editorEl: HTMLDivElement): { query: string; atIndex: number } | null {
  const sel = window.getSelection();
  if (!sel || sel.rangeCount === 0) return null;

  const range = sel.getRangeAt(0);
  const node = range.startContainer;

  if (node.nodeType !== Node.TEXT_NODE) return null;
  const text = node.textContent ?? "";
  const offset = range.startOffset;
  const before = text.slice(0, offset);
  const atIndex = before.lastIndexOf("@");
  if (atIndex === -1) return null;

  // Only activate if @ is at start of word (preceded by space, newline, or start)
  if (atIndex > 0 && !/[\s]/.test(before[atIndex - 1])) return null;

  const query = before.slice(atIndex + 1);
  // Cancel if query contains a space (user moved past the mention)
  if (query.includes(" ")) return null;

  return { query, atIndex };
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map(w => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

export function EditorMentionAutocomplete({ editorRef, onInsert }: Props) {
  const { data: allUsers } = useUsers();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [caretPos, setCaretPos] = useState<CaretRect | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  const filtered: User[] = allUsers
    ? allUsers.filter(u => u.name.toLowerCase().includes(query.toLowerCase())).slice(0, 6)
    : [];

  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;

    const handleInput = () => {
      const result = getQueryBeforeCaret(el);
      if (result) {
        setQuery(result.query);
        setCaretPos(getCaretRect());
        setOpen(true);
        setActiveIndex(0);
      } else {
        setOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return;
      switch (e.key) {
        case "Escape":
          e.preventDefault();
          setOpen(false);
          break;
        case "ArrowDown":
          e.preventDefault();
          setActiveIndex(i => Math.min(i + 1, filtered.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          setActiveIndex(i => Math.max(i - 1, 0));
          break;
        case "Enter":
        case "Tab":
          if (filtered[activeIndex]) {
            e.preventDefault();
            selectUser(filtered[activeIndex]);
          }
          break;
      }
    };

    el.addEventListener("input", handleInput);
    el.addEventListener("keydown", handleKeyDown, { capture: true });
    return () => {
      el.removeEventListener("input", handleInput);
      el.removeEventListener("keydown", handleKeyDown, { capture: true });
    };
  }, [open, filtered, activeIndex, editorRef]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  function selectUser(user: User) {
    onInsert(user);
    setOpen(false);
  }

  if (!open || filtered.length === 0 || !caretPos) return null;

  return (
    <div
      ref={containerRef}
      style={{ position: "fixed", top: caretPos.top + 4, left: caretPos.left, zIndex: 9999 }}
      className="bg-popover border border-border rounded-md shadow-md min-w-48 py-1 text-sm"
    >
      {filtered.map((u, i) => (
        <button
          key={u.id}
          onMouseDown={e => {
            e.preventDefault();
            selectUser(u);
          }}
          className={`w-full text-left px-3 py-1.5 hover:bg-muted flex items-center gap-2 ${
            i === activeIndex ? "bg-muted" : ""
          }`}
        >
          <span className="h-6 w-6 rounded-full bg-primary/10 text-primary text-xs font-semibold flex items-center justify-center flex-shrink-0">
            {getInitials(u.name)}
          </span>
          <span>{u.name}</span>
        </button>
      ))}
    </div>
  );
}
